import type { Program } from '../types';

export function clean(value: unknown) {
  return String(value || '')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^[-—\s]+$|^open page$/i, '')
    .trim();
}

export function slug(value: unknown) {
  return clean(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export function normalizeSearch(value: unknown) {
  return clean(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export function splitSpecializations(value?: string) {
  if (!value) return [];
  return value
    .replace(/\([^)]*specializations?[^)]*\)/gi, '')
    .split(/[,;|/]/)
    .map(clean)
    .filter((item) => item && item.length < 90 && !/^none$/i.test(item))
    .slice(0, 60);
}

export function groupByUniversity(programs: Program[]) {
  return programs.reduce<Record<string, Program[]>>((groups, program) => {
    if (!groups[program.university]) groups[program.university] = [];
    groups[program.university].push(program);
    return groups;
  }, {});
}

export function safe(value?: string | null, fallback = 'Not listed in source sheet') {
  return value && clean(value) ? value : fallback;
}

export function hasValue(value?: string | null) {
  const normalized = clean(value);
  return Boolean(normalized && !/^(not listed|partner-dependent|—|-|n\/a)$/i.test(normalized));
}

export function formatInr(value?: number | null) {
  if (!Number.isFinite(value || NaN)) return 'Not listed';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(value as number);
}

export function unique<T>(items: T[]) {
  return [...new Set(items.filter(Boolean))];
}

export function placementCopy(program: Program) {
  if (hasValue(program.placement)) return program.placement!;
  const combined = `${program.specializations || ''} ${program.accreditation || ''}`;
  const match = combined.match(/[^.;]*(placement|career assistance|career support|pay.after.placement)[^.;]*/i);
  if (match) return match[0].trim();
  return 'Career support and placement assistance are available as per the university process. Verify current services before promising interviews, salary, or job guarantees.';
}

export function scholarshipCopy(program: Program) {
  if (hasValue(program.scholarship)) return `Published waiver or scholarship information: ${program.scholarship}.`;
  const match = `${program.fee || ''} ${program.emi || ''} ${program.specializations || ''}`.match(
    /[^.;]*(scholarship|waiver|discount)[^.;]*/i,
  );
  return match ? match[0].trim() : 'Scholarship or waiver availability should be confirmed for the current intake.';
}
